import {
	Heading,
	Paragraph,
	Popover,
	PopoverHeader,
	TextInput,
} from '@audio-network/componentlibrary'
import React, { useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Button } from '@audio-network/componentlibrary'
import { FormProps } from './AddToTeam.types'
import clientGetToggleFromCookie from '@/utils/cookies/getToggleFromCookie'
import matchByLocale from '@/utils/matchByLocale'
import { sendAnalytics } from '@audio-network/analytics'
import styles from './AddToTeam.module.css'
import { toast } from '@/utils/notifications/toast'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

const AddToTeam = (): JSX.Element => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		clearErrors,
		reset,
	} = useForm<FormProps>({
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	})

	const popoverRef = useRef<HTMLDivElement>(null)

	const { t } = useTranslation(['navigation'])

	const handleAddToTeam: SubmitHandler<FormProps> = async (
		data
	): Promise<void> => {
		try {
			const response = await fetch('/api/musicapp/salesforce/teamRequest', {
				method: 'POST',
				body: new URLSearchParams({ requesteeEmail: data.email }),
			})

			const popover = popoverRef.current
			if (popover) {
				if (
					Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover')
				) {
					// @ts-ignore
					popover.togglePopover()
				} else {
					popover.dataset.showPopover = 'false'
				}
			}

			if (!response.ok) {
				throw new Error('Failed to add to team request')
			}

			reset()
			sendAnalytics({
				noun: 'Add to Team',
				pastTenseVerb: 'Completed',
			})
			toast.success(t('add-to-team-success-message'), {
				'data-testid': 'add-to-team-success-message',
			})
		} catch (error) {
			toast.error(t('add-to-team-error-message'), {
				'data-testid': 'add-to-team-error-message',
			})
		}
	}

	/**
	 * UX testing first and last name fields
	 */
	const { locale } = useRouter()

	const showFirstNameAndLastNameFields =
		matchByLocale(locale, { 'en-GB': true }) &&
		clientGetToggleFromCookie('AddFirstNameLastNameToTeam')

	const FirstAndLastNameFields = (): JSX.Element => {
		return (
			<div className={styles.nameFields}>
				<TextInput
					type="text"
					name="first-name"
					label="First name"
					required
					{...register('firstName', {
						required: 'Please include a first name',
					})}
					error={errors.firstName}
				/>
				<TextInput
					type="text"
					name="last-name"
					label="Last name"
					required
					{...register('lastName', {
						required: 'Please include a last name',
					})}
					error={errors.lastName}
				/>
			</div>
		)
	}

	return (
		<Popover
			className={styles.popover}
			id="add-to-team-popover"
			mobileCloseLabel={t('close')}
			ref={popoverRef}
		>
			<PopoverHeader>
				<Heading level={2} size="S" uppercase>
					{t('add-to-team-heading')}
				</Heading>
				<Paragraph>{t('add-to-team-description')}</Paragraph>
			</PopoverHeader>
			<form
				className={styles.form}
				noValidate
				onSubmit={handleSubmit(handleAddToTeam)}
			>
				{showFirstNameAndLastNameFields && <FirstAndLastNameFields />}
				<TextInput
					type="email"
					label={t('add-to-team-input-label')}
					name="email"
					required
					aria-invalid={errors.email ? 'true' : 'false'}
					{...register('email', {
						onChange: () => clearErrors('email'),
						pattern: {
							value: /^[\w-.]+@([\w-]+\.)+[\w-]{2,63}$/,
							message: t('add-to-team-input-error-message'),
						},
						required: t('add-to-team-input-error-message'),
					})}
					error={errors.email}
				/>
				<Button className={styles.button} variant="primary" type="submit">
					{t('add-to-team-submit-button')}
				</Button>
			</form>
		</Popover>
	)
}

export default AddToTeam
